import { BsdsCard, BsdsCheckbox, BsdsInput, BsdsText } from '@bsds-lib-component/react';
import React, { useId, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { Dropdown, Pagination, SystemMessage, useSystemContainer } from '../../components';
import Button from '../../components/button/Button';
import { CpfInput } from '../../components/input/CpfInput';
import { DocumentNumberInput } from '../../components/input/DocumentNumberInput';
import { EmailInput } from '../../components/input/EmailInput';
import { PhoneInput } from '../../components/input/PhoneInput';
import Visibility from '../../components/visibility/Visibility';
import { SKIP_CORPORATE_EMAIL_STEP } from '../../constantsConfig/constantsConfig';
import { useRegisterPolicyholder } from '../../hooks/useRegisterPolicyholder';
import { apiService } from '../../services';
import { useUserStore } from '../../store/userStore';
import {
  DEFAULT_REPRESENTATIVE_USER_ROLE_CODE,
  REPRESENTATIVE_USER_ROLE_OPTIONS,
} from '../../types/registerPolicyholderType';
import type { BusinessLinkItem, UserPoliciesListItem } from '../../types/serviceType';
import { buildEstpChaveLegado, selectedTypeToRamoDigit } from '../../utils/chaveLegadoUtils';
import { validateDocumentNumber } from '../../utils/documentNumberUtils';
import { validateCpf } from '../../utils/cpfUtils';
import {
  businessLinkRowKey,
  policyListRowKey,
} from '../../utils/registerPolicyholderUtil';

interface RegisterPolicyholderPageProps {
  onClose?: () => void;
}

/** Formats API date strings for display as DD/MM/YYYY (display only). */
const formatOpeningDateForDisplay = (dateStr: string | null | undefined): string => {
  if (!dateStr) return '';
  const trimmed = dateStr.trim();
  // ISO / CADP: `2026-10-30`, `2026-10-30T03:00:00`, `2026-10-30-03:00`
  const ymd = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (ymd) {
    const [, year, month, day] = ymd;
    return `${day}/${month}/${year}`;
  }
  // Vida policy dates: `07-07-2020` (dd-mm-yyyy)
  const dmy = trimmed.match(/^(\d{2})-(\d{2})-(\d{4})/);
  if (dmy) {
    const [, day, month, year] = dmy;
    return `${day}/${month}/${year}`;
  }
  return dateStr;
};

const RegisterPolicyholderPage: React.FC<RegisterPolicyholderPageProps> = ({ onClose }) => {
  const navigate = useNavigate();
  const { setLoading } = useSystemContainer();
  const mainContentId = useId();
  const typeSelectionId = useId();
  const cnpjStepId = useId();
  const cpfStepId = useId();
  const vinculeStepId = useId();
  const errorMessageId = useId();
  const successMessageId = useId();
  const user = useUserStore((s) => s.user);
  const {
    isLoading,
    error,
    cpfData,
    selectedType,
    showCpfSearch,
    showCpfInput,
    cnpjData,
    searchedCnpj,
    isSuccess,
    pfExistedInCadp,
    policyListRows,
    businessLinkRows,
    cpfBusinessLinkRows,
    selectType,
    getPolicyholderByCNPJ,
    getPolicyholderByCPF,
    continueAfterCpfStep,
    finalizeRepresentativeLinks,
    goBack,
    goToCpfSearch,
    checkVinculoParaApolice,
  } = useRegisterPolicyholder();

  // Sync loading state with SystemContainer
  React.useEffect(() => {
    setLoading(isLoading);
  }, [isLoading, setLoading]);

  const [cnpjSearch, setCnpjSearch] = useState('');
  const [cpfSearch, setCpfSearch] = useState('');
  const [cpfInput, setCpfInput] = useState('');
  const [isCnpjValid, setIsCnpjValid] = useState(false);
  const [isCpfValid, setIsCpfValid] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    corporateEmail: '',
    phone: '',
    birthDate: '',
    biologicalSex: '',
    nomeMae: '',
    nomePai: '',
    estadoCivilId: '',
    grauEscolaridadeId: '',
    position: '',
    rg: '',
    issuingAgency: '',
    issueDate: '',
  });

  const [civilStatusOptions, setCivilStatusOptions] = useState<{ label: string; value: string }[]>([]);
  const [educationOptions, setEducationOptions] = useState<{ label: string; value: string }[]>([]);

  type BusinessLink = {
    id: string;
    number: string;
    description: string;
    origin: string;
  };

  /** Maps row id (`representacao.id`) → policy row key */
  const [vinculoPolicyKeys, setVinculoPolicyKeys] = useState<Record<string, string>>({});
  /** Maps row id → papel do usuário (9=Estipulante, 10=Sub-estipulante) */
  const [vinculoPapelKeys, setVinculoPapelKeys] = useState<Record<string, string>>({});
  /** When there are no corporate (PJ) links: policies selected to create representation */
  const [selectedNewPolicyKeys, setSelectedNewPolicyKeys] = useState<string[]>([]);
  const [papelUsuarioId, setPapelUsuarioId] = useState<string>(DEFAULT_REPRESENTATIVE_USER_ROLE_CODE);
  /** After choosing a policy: validation by chaveLegado (CNPJ + CPF filtered) */
  const [policyCheckByVinculoRow, setPolicyCheckByVinculoRow] = useState<
    Record<string, { cpfJaVinculado: boolean; checking: boolean }>
  >({});
  const [policyCheckByNewKey, setPolicyCheckByNewKey] = useState<
    Record<string, { cpfJaVinculado: boolean; checking: boolean }>
  >({});

  // Step control: 1 = CNPJ, 2 = CPF, 3 = policy linking
  const [currentStep, setCurrentStep] = useState<1 | 2 | 3>(1);

  // Pagination state
  const ITEMS_PER_PAGE = 6;
  const [currentPage, setCurrentPage] = useState<number>(1);

  const businessLinksForTable: BusinessLink[] = policyListRows.map((row, idx) => ({
    id: policyListRowKey(row, idx),
    number: row.numApoliceApol != null ? String(row.numApoliceApol) : '—',
    description: row.nomeProdutoApol ?? row.nomeRamoApol ?? '—',
    origin: row.nomeEmpresaApol ?? row.nomeEstipulanteApol ?? '',
  }));

  // Calculate pagination (client-side pagination, similar to PaymentTab pattern but client-side)
  const totalPages = Math.max(1, Math.ceil(businessLinksForTable.length / ITEMS_PER_PAGE));
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const paginatedBusinessLinks = React.useMemo(() => {
    return businessLinksForTable.slice(startIndex, endIndex);
  }, [businessLinksForTable, startIndex, endIndex]);

  React.useEffect(() => {
    setCurrentPage(1);
  }, [businessLinksForTable.length]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  React.useEffect(() => {
    if (currentStep !== 2 || !showCpfInput) return;
    let cancelled = false;
    (async () => {
      try {
        const [civil, edu] = await Promise.all([
          apiService.enum.getCivilStatus(),
          apiService.enum.getEducationLevels(),
        ]);
        if (cancelled) return;
        setCivilStatusOptions(
          (civil.estadosCivis ?? []).map((e) => ({ label: e.descricao, value: e.id }))
        );
        setEducationOptions(
          (edu.grausEscolaridade ?? []).map((g) => ({ label: g.descricao, value: g.id }))
        );
      } catch {
        /* optional enums */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentStep, showCpfInput]);

  React.useEffect(() => {
    if (cpfData) {
      setFormData({
        name: cpfData.name || '',
        email: cpfData.email || '',
        corporateEmail: cpfData.corporateEmail || '',
        phone: cpfData.phone || '',
        birthDate: cpfData.birthDate || '',
        biologicalSex: cpfData.biologicalSex || '',
        nomeMae: cpfData.nomeMae || '',
        nomePai: cpfData.nomePai || '',
        estadoCivilId: cpfData.estadoCivilId || '',
        grauEscolaridadeId: cpfData.grauEscolaridadeId || '',
        position: cpfData.position || '',
        rg: cpfData.rg || '',
        issuingAgency: cpfData.issuingAgency || '',
        issueDate: cpfData.issueDate || '',
      });
    }
  }, [cpfData]);

  // Validate document (CPF / CNPJ / CNPJ alfanumérico) whenever the value changes
  React.useEffect(() => {
    const isValid = cnpjSearch.trim() !== '' && validateDocumentNumber(cnpjSearch);
    setIsCnpjValid(isValid);
  }, [cnpjSearch]);

  // Validate CPF whenever the value changes
  React.useEffect(() => {
    const isValid = cpfInput.trim() !== '' && validateCpf(cpfInput);
    setIsCpfValid(isValid);
  }, [cpfInput]);

  const focusInputByAriaLabel = React.useCallback((ariaLabel: string) => {
    const el = document.querySelector<HTMLElement>(`[aria-label="${ariaLabel}"]`);
    if (!el) return;

    const bsds = el as HTMLElement & { setFocus?: () => void | Promise<void> };
    if (typeof bsds.setFocus === 'function') {
      void bsds.setFocus();
      return;
    }

    const nestedInput =
      (el.shadowRoot?.querySelector('input') as HTMLInputElement | null) ??
      (el.querySelector('input') as HTMLInputElement | null);
    if (nestedInput) {
      nestedInput.focus();
      return;
    }

    el.focus();
  }, []);

  // Ensures the cursor is placed on the correct field when switching steps.
  React.useEffect(() => {
    if (isLoading || isSuccess) return;

    if (currentStep === 1 && !!selectedType && !showCpfInput) {
      const t = window.setTimeout(() => focusInputByAriaLabel('Número do Documento Pessoa Jurídica'), 100);
      return () => window.clearTimeout(t);
    }

    if (currentStep === 2 && showCpfInput) {
      const t = window.setTimeout(() => focusInputByAriaLabel('CPF do Segurado'), 100);
      return () => window.clearTimeout(t);
    }

    return undefined;
  }, [currentStep, focusInputByAriaLabel, isLoading, isSuccess, selectedType, showCpfInput]);

  // Sync CNPJ search field with searched CNPJ from hook
  React.useEffect(() => {
    if (searchedCnpj && !cnpjSearch) {
      setCnpjSearch(searchedCnpj);
    }
  }, [searchedCnpj, cnpjSearch]);

  const handleCnpjSearch = async () => {
    if (!cnpjSearch.trim()) return;
    await getPolicyholderByCNPJ(cnpjSearch);
  };

  const handleCpfInputSearch = async () => {
    if (!cpfInput.trim()) return;
    // New CPF search: reset Situação CPF / pairing checks from the previous person.
    setVinculoPolicyKeys({});
    setVinculoPapelKeys({});
    setSelectedNewPolicyKeys([]);
    setPolicyCheckByVinculoRow({});
    setPolicyCheckByNewKey({});
    await getPolicyholderByCPF(cpfInput);
  };

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleBsdsInputChange = (field: string) => (event: React.FormEvent<HTMLBsdsInputElement>) => {
    const target = event.target as HTMLBsdsInputElement;
    const value = (event as React.FormEvent<HTMLBsdsInputElement> & { detail?: { value: string } }).detail?.value || target?.value || '';
    handleInputChange(field, String(value));
  };

  const extractContractNumberFromChaveLegado = React.useCallback((chaveLegado?: string) => {
    if (!chaveLegado) return '—';
    const parts = chaveLegado.split('.');
    if (parts.length < 4) return '—';
    const contractNumber = parts[2]?.trim() ?? '';
    return contractNumber || '—';
  }, []);

  const findPolicyByRowKey = React.useCallback(
    (key: string) => {
      const idx = policyListRows.findIndex((row, i) => policyListRowKey(row, i) === key);
      return idx >= 0 ? policyListRows[idx] : undefined;
    },
    [policyListRows]
  );

  const buildPairings = (): Array<{
    vinculo: BusinessLinkItem;
    policy: UserPoliciesListItem;
    papelPessoaSecundario: string;
  }> => {
    return businessLinkRows
      .map((v) => {
        const vid = businessLinkRowKey(v);
        const pk = vinculoPolicyKeys[vid];
        if (!pk) return null;
        const policy = findPolicyByRowKey(pk);
        if (!policy) return null;
        return {
          vinculo: v,
          policy,
          papelPessoaSecundario: vinculoPapelKeys[vid] ?? DEFAULT_REPRESENTATIVE_USER_ROLE_CODE,
        };
      })
      .filter(
        (p): p is { vinculo: BusinessLinkItem; policy: UserPoliciesListItem; papelPessoaSecundario: string } =>
          p != null
      );
  };

  const handleFinalize = async () => {
    if (!user?.idPessoa) {
      return;
    }
    if (businessLinkRows.length > 0) {
      await finalizeRepresentativeLinks(
        { existingPairings: buildPairings(), newRepresentations: [] },
        user.idPessoa
      );
      return;
    }
    const newReps = selectedNewPolicyKeys
      .map((key) => {
        const pol = findPolicyByRowKey(key);
        return pol ? { policy: pol, papelId: papelUsuarioId } : null;
      })
      .filter((x): x is { policy: UserPoliciesListItem; papelId: string } => x != null);
    await finalizeRepresentativeLinks(
      { existingPairings: [], newRepresentations: newReps },
      user.idPessoa
    );
  };

  const cpfRepIdSet = React.useMemo(
    () =>
      new Set(
        cpfBusinessLinkRows
          .map((v) => v.representacao?.id)
          .filter((id): id is string => id != null && id !== '')
      ),
    [cpfBusinessLinkRows]
  );

  const vinculoPolicyKeysRef = React.useRef(vinculoPolicyKeys);
  vinculoPolicyKeysRef.current = vinculoPolicyKeys;

  const step3VinculoBlocked = React.useMemo(() => {
    const byRow = businessLinkRows.some((v) => {
      const rowId = businessLinkRowKey(v);
      const pk = vinculoPolicyKeys[rowId];
      return Boolean(pk) && policyCheckByVinculoRow[rowId]?.cpfJaVinculado === true;
    });
    const byNew = selectedNewPolicyKeys.some(
      (k) => policyCheckByNewKey[k]?.cpfJaVinculado === true
    );
    return byRow || byNew;
  }, [
    businessLinkRows,
    vinculoPolicyKeys,
    policyCheckByVinculoRow,
    selectedNewPolicyKeys,
    policyCheckByNewKey,
  ]);

  const step3PairingComplete =
    policyListRows.length > 0 &&
    !step3VinculoBlocked &&
    (businessLinkRows.length > 0
      ? businessLinkRows.some((v) => {
          const k = businessLinkRowKey(v);
          const pk = vinculoPolicyKeys[k];
          if (pk == null || pk === '') return false;
          const repId = v.representacao?.id;
          if (repId != null && cpfRepIdSet.has(repId)) return false;
          const check = policyCheckByVinculoRow[k];
          // Enable Finalizar only after chaveLegado check finishes without block
          if (!check || check.checking || check.cpfJaVinculado) return false;
          return true;
        })
      : selectedNewPolicyKeys.length > 0 && papelUsuarioId !== '');

  const businessLinksIdentity = React.useMemo(
    () => businessLinkRows.map((v) => businessLinkRowKey(v)).join('|'),
    [businessLinkRows]
  );

  React.useEffect(() => {
    setVinculoPolicyKeys({});
    setVinculoPapelKeys({});
    setPolicyCheckByVinculoRow({});
  }, [businessLinksIdentity]);

  React.useEffect(() => {
    setSelectedNewPolicyKeys([]);
    setPolicyCheckByNewKey({});
  }, [businessLinkRows.length, policyListRows.length]);

  const cnpjDigitsForChave = React.useMemo(
    () => (cnpjData?.cnpj ?? '').replace(/[^a-zA-Z0-9]/g, '').toUpperCase(),
    [cnpjData?.cnpj]
  );

  const handleCancel = () => {
    if (onClose) {
      onClose();
    } else {
      navigate('/home');
    }
  };

  const resetFormData = () => {
    setCnpjSearch('');
    setCpfSearch('');
    setCpfInput('');
    setIsCnpjValid(false);
    setIsCpfValid(false);
    setVinculoPolicyKeys({});
    setVinculoPapelKeys({});
    setSelectedNewPolicyKeys([]);
    setPapelUsuarioId(DEFAULT_REPRESENTATIVE_USER_ROLE_CODE);
    setPolicyCheckByVinculoRow({});
    setPolicyCheckByNewKey({});
    setFormData({
      name: '',
      email: '',
      corporateEmail: '',
      phone: '',
      birthDate: '',
      biologicalSex: '',
      nomeMae: '',
      nomePai: '',
      estadoCivilId: '',
      grauEscolaridadeId: '',
      position: '',
      rg: '',
      issuingAgency: '',
      issueDate: '',
    });
  };

  const handleContinue = () => {
    // Go to next step (CPF)
    setCurrentStep(2);
    goToCpfSearch();
  };

  const handleContinueToVincule = async () => {
    const snapshot = {
      name: formData.name,
      email: formData.email,
      corporateEmail: formData.corporateEmail,
      phone: formData.phone,
      birthDate: formData.birthDate,
      biologicalSex: formData.biologicalSex,
      nomeMae: formData.nomeMae,
      nomePai: formData.nomePai,
      estadoCivilId: formData.estadoCivilId,
      grauEscolaridadeId: formData.grauEscolaridadeId,
    };
    const ok = await continueAfterCpfStep(snapshot);
    if (ok) {
      setCurrentStep(3);
    }
  };

  const handleBackFromCpf = () => {
    // Go back to CNPJ step
    setCurrentStep(1);
    goBack();
  };

  const handleBackFromVincule = () => {
    // Keep loaded CPF/vínculo data; Situação CPF refreshes only when Buscar CPF runs.
    setCurrentStep(2);
  };

  const handleTypeChange = (type: 'vida' | 'previdencia') => {
    // Reset form data when changing type
    resetFormData();
    setCurrentStep(1);

    // If we're in CPF input stage, go back to CNPJ search
    if (showCpfInput) {
      goBack();
    }

    // Select the new type (this will set showCnpjSearch to true)
    selectType(type);
    window.setTimeout(() => focusInputByAriaLabel('Número do Documento Pessoa Jurídica'), 100);
  };

  const isCpfFoundInCadp = Boolean(cpfData) && pfExistedInCadp;
  const isCadpReadOnly = isCpfFoundInCadp;

  return (
    <div
      role="main"
      aria-busy={isLoading}
      aria-label="Página de cadastro de Segurado"
      id={mainContentId}
    >
      <div className="bsds-width-100 flex flex-col min-h-[calc(100vh-9rem)]">
        {error && (
          <div
            id={errorMessageId}
            role="alert"
            aria-live="assertive"
            aria-atomic="true"
          >
            <SystemMessage
              message={error}
              type="error"
            />
          </div>
        )}

        {isSuccess && (
          <div
            id={successMessageId}
            role="status"
            aria-live="polite"
            aria-atomic="true"
          >
            <SystemMessage
              message="Segurado cadastrado com sucesso!"
              type="success"
            />
          </div>
        )}

        {/* Type selection */}
        <div
          id={typeSelectionId}
          className="bsds-mb-16"
          role="region"
          aria-label="Seleção de ramo"
        >
          <BsdsText>Ramo</BsdsText>
          <div
            className="bsds-display-flex bsds-justify-content-start bsds-align-items-center bsds-gap-24"
            role="radiogroup"
            aria-label="Selecione o Ramo do Segurado"
          >
            <div className="bsds-display-flex bsds-align-items-center bsds-gap-8 bsds-pr-48">
              <BsdsCheckbox
                checked={selectedType === 'vida'}
                onBsdsChange={() => handleTypeChange('vida')}
                color="primary"
                disabled={isSuccess}
                aria-label="Ramo Vida"
              />
              <span className="bsds-text-regular bsds-pl-12">Vida</span>
            </div>
            <div className="bsds-display-flex bsds-align-items-center bsds-gap-8">
              <BsdsCheckbox
                checked={selectedType === 'previdencia'}
                onBsdsChange={() => handleTypeChange('previdencia')}
                color="primary"
                disabled={isSuccess}
                aria-label="Ramo Previdência"
              />
              <span className="bsds-text-regular bsds-pl-12">Previdência</span>
            </div>
          </div>
        </div>

        {/* Step 1: CNPJ search */}
        <Visibility
          isActive={currentStep === 1 && !!(selectedType && !showCpfInput)}
          animated
          animationDuration={250}
        >
          <div
            id={cnpjStepId}
            className="bsds-mb-24"
            role="region"
            aria-label="Passo 1: Buscar Número do Documento Pessoa Jurídica"
          >
            <div className="bsds-display-flex bsds-justify-content-center bsds-align-items-start bsds-gap-16">
              <DocumentNumberInput
                value={cnpjSearch}
                onChange={setCnpjSearch}
                label="Número do Documento Pessoa Jurídica"
                placeholder="Digite o documento"
                disabled={isLoading || isSuccess}
                aria-label="Número do Documento Pessoa Jurídica"
                aria-describedby={`${cnpjStepId}-help`}
                aria-required
              />
              <span
                id={`${cnpjStepId}-help`}
                className="absolute w-px h-px p-0 -m-px overflow-hidden whitespace-nowrap border-0"
                style={{ clip: 'rect(0, 0, 0, 0)', clipPath: 'inset(50%)' }}
              >
                Digite o CPF, CNPJ ou CNPJ alfanumérico da pessoa jurídica para buscar os dados cadastrais
              </span>

              <Button
                onClick={handleCnpjSearch}
                disabled={isLoading || !cnpjSearch.trim() || !isCnpjValid || isSuccess}
                type="button"
                color="primary"
                fill="solid"
                className="bsds-width-25 ml-4 mt-6 md:mt-7.5"
                aria-label={
                  isLoading
                    ? 'Buscando documento...'
                    : !cnpjSearch.trim() || !isCnpjValid
                      ? 'Preencha um documento válido para buscar'
                      : 'Buscar dados da empresa pelo documento'
                }
              >
                Buscar
              </Button>
            </div>
          </div>
        </Visibility>


        {/* Step 1: Company data (CNPJ found) */}
        <Visibility
          isActive={currentStep === 1 && !!(cnpjData && !showCpfSearch && !showCpfInput)}
          animated
          animationDuration={300}
        >
          <div
            className="bsds-width-100 mb-6"
            role="region"
            aria-label="Dados da empresa encontrados"
          >
            <div className="flex flex-col lg:flex-row lg:flex-wrap space-y-4">

              {/* Corporate Name */}
              <div className="w-full lg:w-1/1">
                <BsdsInput
                  value={cnpjData?.corporateName || ''}
                  disabled
                  label="Razão Social"
                  type="text"
                  platform="web"
                  color="primary"
                />
              </div>

              {/* Fantasy Name */}
              <div className="w-full lg:w-1/1">
                <BsdsInput
                  value={cnpjData?.fantasyName || ''}
                  disabled
                  label="Nome Fantasia"
                  type="text"
                  platform="web"
                  color="primary"
                />
              </div>

              {/* Opening Date */}
              <div className="w-full lg:w-1/3 lg:pr-4">
                <BsdsInput
                  value={formatOpeningDateForDisplay(cnpjData?.openingDate)}
                  disabled
                  label="Data de Abertura"
                  type="text"
                  platform="web"
                  color="primary"
                />
              </div>

              {/* Email */}
              <div className="w-full lg:w-1/3 lg:pr-4">
                <BsdsInput
                  value={cnpjData?.email || ''}
                  disabled
                  label="E-mail"
                  type="email"
                  platform="web"
                  color="primary"
                />
              </div>

              {/* Phone */}
              <div className="w-full lg:w-1/3">
                <BsdsInput
                  value={cnpjData?.phone || ''}
                  disabled
                  label="Telefone"
                  type="tel"
                  platform="web"
                  color="primary"
                />
              </div>

            </div>
          </div>
        </Visibility>

        {/* Step 2: CPF search */}
        <Visibility
          isActive={currentStep === 2 && !!showCpfInput}
          animated
          animationDuration={250}
        >
          <div
            id={cpfStepId}
            className="bsds-mb-24"
            role="region"
            aria-label="Passo 2: Buscar CPF do Segurado"
          >
            <div className="bsds-display-flex bsds-justify-content-center bsds-align-items-start bsds-gap-16">
              <CpfInput
                value={cpfInput}
                onChange={setCpfInput}
                placeholder="Digite o CPF"
                disabled={isLoading || isSuccess}
                label="CPF"
                aria-label="CPF do Segurado"
                aria-describedby={`${cpfStepId}-help`}
                aria-required
              />
              <span
                id={`${cpfStepId}-help`}
                className="absolute w-px h-px p-0 -m-px overflow-hidden whitespace-nowrap border-0"
                style={{ clip: 'rect(0, 0, 0, 0)', clipPath: 'inset(50%)' }}
              >
                Digite o CPF do Segurado para buscar ou cadastrar os dados
              </span>
              <Button
                onClick={handleCpfInputSearch}
                disabled={isLoading || !cpfInput.trim() || !isCpfValid || isSuccess}
                type="button"
                color="primary"
                fill="solid"
                className="bsds-width-25 ml-4 mt-6 md:mt-7.5"
                aria-label={
                  isLoading
                    ? 'Buscando CPF...'
                    : !cpfInput.trim() || !isCpfValid
                      ? 'Preencha um CPF válido para buscar'
                      : 'Buscar dados do Segurado pelo CPF'
                }
              >
                Buscar CPF
              </Button>
            </div>
          </div>
        </Visibility>

        {/* Step 2: Filling fields (CPF) */}
        <Visibility
          isActive={currentStep === 2 && !!(showCpfInput && cpfData)}
          animated
          animationDuration={300}
        >
          <div
            className="bsds-width-100 mb-6"
            role="region"
            aria-label="Formulário de dados do Segurado"
          >
            <div className="flex flex-col lg:flex-row lg:flex-wrap space-y-4">
              {isCpfFoundInCadp ? (
                <>
                  {/* Fields aligned with recuperar-pessoa-fisica response + contacts */}
                  <div className="w-full lg:w-1/1">
                    <BsdsInput
                      value={formData.name}
                      onChange={handleBsdsInputChange('name')}
                      disabled={isLoading || isSuccess || isCadpReadOnly}
                      label="Nome Completo"
                      type="text"
                      platform="web"
                      color="primary"
                      required
                    />
                  </div>

                  <div className="w-full lg:w-1/3 lg:pr-4">
                    <EmailInput
                      value={formData.email}
                      onChange={(value: string) => handleInputChange('email', value)}
                      disabled={isLoading || isSuccess || isCadpReadOnly}
                      label="E-mail"
                      required
                    />
                  </div>

                  <div className="w-full lg:w-1/3 lg:pr-4">
                    <EmailInput
                      value={formData.corporateEmail}
                      onChange={(value: string) => handleInputChange('corporateEmail', value)}
                      disabled={
                        isLoading ||
                        isSuccess ||
                        Boolean(cpfData?.hasCorporateEmailFromContacts)
                      }
                      label="E-mail Corporativo"
                      company
                      required
                    />
                  </div>

                  <div className="w-full lg:w-1/3">
                    <PhoneInput
                      value={formData.phone}
                      onChange={(value) => handleInputChange('phone', value)}
                      disabled={isLoading || isSuccess || isCadpReadOnly}
                      label="Telefone"
                    />
                  </div>

                  <div className="w-full lg:w-1/3 lg:pr-4">
                    <BsdsInput
                      value={formData.birthDate}
                      onChange={handleBsdsInputChange('birthDate')}
                      disabled={isLoading || isSuccess || isCadpReadOnly}
                      label="Data de Nascimento"
                      type="date"
                      platform="web"
                      color="primary"
                    />
                  </div>

                  <div className="w-full lg:w-1/3 lg:pr-4">
                    <Dropdown
                      id="biological-sex"
                      label="Sexo Biológico"
                      placeholder="Selecione o sexo"
                      value={formData.biologicalSex}
                      onChange={(value) => handleInputChange('biologicalSex', value)}
                      disabled={isLoading || isSuccess || isCadpReadOnly}
                      options={[
                        { label: 'Masculino', value: 'Masculino' },
                        { label: 'Feminino', value: 'Feminino' },
                        { label: 'Ver o nome', value: 'Ver o nome' },
                      ]}
                    />
                  </div>

                  <div className="w-full lg:w-1/2 lg:pr-4">
                    <BsdsInput
                      value={formData.nomeMae}
                      onChange={handleBsdsInputChange('nomeMae')}
                      disabled={isLoading || isSuccess || isCadpReadOnly}
                      label="Nome da Mãe"
                      type="text"
                      platform="web"
                      color="primary"
                    />
                  </div>

                  <div className="w-full lg:w-1/2 lg:pr-4">
                    <Dropdown
                      id="estado-civil"
                      label="Estado civil"
                      placeholder="Selecione"
                      value={formData.estadoCivilId}
                      onChange={(value) => handleInputChange('estadoCivilId', value)}
                      disabled={isLoading || isSuccess || isCadpReadOnly}
                      options={civilStatusOptions}
                      aria-label="Estado civil"
                    />
                  </div>

                  <div className="w-full lg:w-1/2">
                    <Dropdown
                      id="grau-escolaridade"
                      label="Grau de escolaridade"
                      placeholder="Selecione"
                      value={formData.grauEscolaridadeId}
                      onChange={(value) => handleInputChange('grauEscolaridadeId', value)}
                      disabled={isLoading || isSuccess || isCadpReadOnly}
                      options={educationOptions}
                      aria-label="Grau de escolaridade"
                    />
                  </div>
                </>
              ) : (
                <>
                  {/* Fields aligned with incluir-dados-basicos-pessoa-fisica request body */}
                  <div className="w-full lg:w-1/1">
                    <BsdsInput
                      value={formData.name}
                      onChange={handleBsdsInputChange('name')}
                      disabled={isLoading || isSuccess}
                      label="Nome Completo"
                      type="text"
                      platform="web"
                      color="primary"
                      required
                    />
                  </div>

                  <div className="w-full lg:w-1/3 lg:pr-4">
                    <EmailInput
                      value={formData.email}
                      onChange={(value: string) => handleInputChange('email', value)}
                      disabled={isLoading || isSuccess}
                      label="E-mail"
                      required
                    />
                  </div>

                  <div className="w-full lg:w-1/3">
                    <PhoneInput
                      value={formData.phone}
                      onChange={(value) => handleInputChange('phone', value)}
                      disabled={isLoading || isSuccess}
                      label="Telefone"
                    />
                  </div>

                  <div className="w-full lg:w-1/2 lg:pr-4">
                    <BsdsInput
                      value={formData.nomeMae}
                      onChange={handleBsdsInputChange('nomeMae')}
                      disabled={isLoading || isSuccess}
                      label="Nome da Mãe"
                      type="text"
                      platform="web"
                      color="primary"
                      required
                    />
                  </div>

                  <div className="w-full lg:w-1/3 lg:pr-4">
                    <BsdsInput
                      value={formData.birthDate}
                      onChange={handleBsdsInputChange('birthDate')}
                      disabled={isLoading || isSuccess}
                      label="Data de Nascimento"
                      type="date"
                      platform="web"
                      color="primary"
                      required
                    />
                  </div>
                </>
              )}
            </div>
          </div>

        </Visibility>

        {/* Step 3: links  + policies — pair each left row with a policy on the right */}
        <Visibility
          isActive={currentStep === 3}
          animated
          animationDuration={300}
        >
          <div
            id={vinculeStepId}
            className="bsds-width-100 mb-6"
            role="region"
            aria-label="Passo 3: Vincular representante — vínculos e Apólices"
          >
            <div className="flex flex-col gap-6 bsds-mt-4">
              {businessLinkRows.length > 0 ? (
                <div className="flex items-center">
                <div className="w-[2.5px] h-8 bg-red-600 bsds-mr-12" aria-hidden="true" />
                <h4 className="text-md font-semibold text-text-color">
                  Vínculos de <span className="font-bold">Negócio</span>
                </h4>
                </div>
              ) : policyListRows.length > 0 ? (
                <p className="text-sm text-text-color">
                  Não há representação de negócio cadastrada para o documento e produto informados. Selecione o papel do usuário, uma ou mais Apólices e confira o preview da chave legado. Ao finalizar, a representação será criada e o CPF vinculado.
                </p>
              ) : (
                <p className="text-sm text-text-color text-center">
                    Este documento não possui Apólices listadas para o filtro atual. Não é possível criar um vínculo de negócio.
                </p>
              )}

              {businessLinkRows.length === 0 && policyListRows.length > 0 && (
                <div className="max-w-xs">
                  <Dropdown
                    id="papel-usuario-nova-representacao"
                    label="Papel Usuario"
                    placeholder="Selecione"
                    value={papelUsuarioId}
                    onChange={(value) => setPapelUsuarioId(value)}
                    disabled={isLoading || isSuccess}
                    options={[...REPRESENTATIVE_USER_ROLE_OPTIONS]}
                    aria-label="Papel do usuário para vincular representante"
                  />
                </div>
              )}

              <div className="flex flex-col gap-6 w-full">
                {businessLinkRows.length > 0 ? (
                  <BsdsCard
                    className="w-full min-w-0 p-4"
                    role="region"
                    aria-label="Vínculos de negócio (listar-vinculo-negocio filtrados)"
                  >
                    <div className="bsds-display-flex bsds-align-items-center bsds-mb-16">
                      <h5 className="opacity-80 bsds-mb-0">Representações existentes (Documento informado)</h5>
                    </div>
                    <div className="w-full min-h-[150px] overflow-x-auto custom-scrollbar rounded-md shadow-xs">
                      <div>
                        <table
                          className="min-w-[1100px] w-full"
                          role="table"
                          aria-label="Representações existentes do documento informado"
                        >
                          <thead>
                            <tr>
                              <th className="px-4 py-2 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap" scope="col">Segmento</th>
                              <th className="px-4 py-2 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap" scope="col">Id representação</th>
                              <th className="px-4 py-2 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap" scope="col">Nº contrato</th>
                              <th className="px-4 py-2 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap" scope="col">Situação CPF</th>
                              <th className="px-4 py-2 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap" scope="col">Situação</th>
                              <th className="px-4 py-2 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap" scope="col">Início vig.</th>
                              <th className="px-4 py-2 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap min-w-[160px]" scope="col">Papel Usuario</th>
                              <th className="px-4 py-2 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap min-w-[200px]" scope="col">Apólice</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-50 text-text-color">
                            {businessLinkRows.map((v) => {
                              const rowId = businessLinkRowKey(v);
                              const selectedKey = vinculoPolicyKeys[rowId] ?? '';
                              const linked = selectedKey ? findPolicyByRowKey(selectedKey) : undefined;
                              const repId = v.representacao?.id;
                              const checkingChave = policyCheckByVinculoRow[rowId]?.checking === true;
                              const granularCpf = selectedKey
                                ? policyCheckByVinculoRow[rowId]?.cpfJaVinculado === true
                                : false;
                              const legacyCpf = repId != null && cpfRepIdSet.has(repId);
                              const cpfJaVinculado = legacyCpf || granularCpf;
                              const selectedPapel =
                                vinculoPapelKeys[rowId] ?? DEFAULT_REPRESENTATIVE_USER_ROLE_CODE;
                              return (
                                <tr key={rowId} className="hover:bg-gray-50 transition-colors text-xs">
                                  <td className="px-4 py-2 align-top">{v.segmento?.descricao ?? '—'}</td>
                                  <td className="px-4 py-2 align-top">
                                    <span className="block">{v.representacao?.id ?? '—'}</span>
                                    {v.representacao?.chaveLegado ? (
                                      <span className="block text-[10px] text-gray-500 mt-0.5 break-all" title={v.representacao.chaveLegado}>
                                        {v.representacao.chaveLegado}
                                      </span>
                                    ) : null}
                                  </td>
                                  <td className="px-4 py-2 align-top">
                                    {extractContractNumberFromChaveLegado(v.representacao?.chaveLegado)}
                                  </td>
                                  <td className="px-4 py-2 align-top whitespace-nowrap">
                                    {checkingChave ? (
                                      <span className="text-gray-600">Verificando vínculo…</span>
                                    ) : cpfJaVinculado ? (
                                      <span className="text-red-700 font-medium">CPF já vinculado</span>
                                    ) : (
                                      <span className="text-green-800 font-medium">Disponível</span>
                                    )}
                                  </td>
                                  <td className="px-4 py-2 align-top">{v.representacao?.situacaoNegocio?.descricao ?? '—'}</td>
                                  <td className="px-4 py-2 align-top whitespace-nowrap">
                                    {formatOpeningDateForDisplay(v.representacao?.dataInicioVigencia) || '—'}
                                  </td>
                                  <td className="px-4 py-2 align-top">
                                    <label className="sr-only" htmlFor={`papel-usuario-${rowId}`}>
                                      Papel Usuario para vínculo {v.representacao?.id}
                                    </label>
                                    <select
                                      id={`papel-usuario-${rowId}`}
                                      className="w-full max-w-[180px] border border-gray-200 rounded px-2 py-1 bg-white"
                                      value={selectedPapel}
                                      disabled={isLoading || isSuccess || legacyCpf}
                                      onChange={(e) => {
                                        const val = e.target.value;
                                        setVinculoPapelKeys((prev) => ({ ...prev, [rowId]: val }));
                                      }}
                                    >
                                      {REPRESENTATIVE_USER_ROLE_OPTIONS.map((opt) => (
                                        <option key={opt.value} value={opt.value}>
                                          {opt.label}
                                        </option>
                                      ))}
                                    </select>
                                  </td>
                                  <td className="px-4 py-2 align-top relative z-10">
                                    <label className="sr-only" htmlFor={`policy-select-${rowId}`}>
                                      Apólice para vínculo {v.representacao?.id}
                                    </label>
                                    <select
                                      id={`policy-select-${rowId}`}
                                      className="w-full max-w-[220px] border border-gray-200 rounded px-2 py-1 bg-white"
                                      value={selectedKey}
                                      disabled={
                                        isLoading ||
                                        isSuccess ||
                                        policyListRows.length === 0 ||
                                        legacyCpf
                                      }
                                      onMouseDown={(e) => e.stopPropagation()}
                                      onClick={(e) => e.stopPropagation()}
                                      onChange={(e) => {
                                        const val = e.target.value;
                                        void (async () => {
                                          setVinculoPolicyKeys((prev) => ({ ...prev, [rowId]: val }));
                                          if (!val) {
                                            setPolicyCheckByVinculoRow((p) => {
                                              const next = { ...p };
                                              delete next[rowId];
                                              return next;
                                            });
                                            return;
                                          }
                                          const pol = findPolicyByRowKey(val);
                                          if (!pol?.numApoliceApol) return;
                                          setPolicyCheckByVinculoRow((p) => ({
                                            ...p,
                                            [rowId]: { cpfJaVinculado: false, checking: true },
                                          }));
                                          const res = await checkVinculoParaApolice(pol);
                                          // Ignore stale response if the user already changed the policy
                                          if (vinculoPolicyKeysRef.current[rowId] !== val) return;
                                          setPolicyCheckByVinculoRow((p) => ({
                                            ...p,
                                            [rowId]: {
                                              cpfJaVinculado: res?.cpfJaVinculadoNestaChave ?? false,
                                              checking: false,
                                            },
                                          }));
                                        })();
                                      }}
                                    >
                                      <option value="">Selecione a Apólice…</option>
                                      {policyListRows.map((row, idx) => {
                                        const optKey = policyListRowKey(row, idx);
                                        const label = row.numApoliceApol != null ? String(row.numApoliceApol) : '—';
                                        return (
                                          <option key={optKey} value={optKey}>
                                            {label}
                                          </option>
                                        );
                                      })}
                                    </select>
                                    {linked?.numApoliceApol != null && !cpfJaVinculado && !checkingChave && (
                                      <span className="block mt-1 text-text-color" aria-live="polite">
                                        Apólice nº {linked.numApoliceApol}
                                        {selectedType && linked.numApoliceApol != null && (cnpjDigitsForChave.length === 11 || cnpjDigitsForChave.length === 14) ? (
                                          <span className="block text-[10px] text-gray-600 mt-1 break-all">
                                            Chave: {buildEstpChaveLegado(
                                              cnpjDigitsForChave,
                                              linked.numApoliceApol,
                                              selectedTypeToRamoDigit(selectedType)
                                            )}
                                          </span>
                                        ) : null}
                                      </span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </BsdsCard>
                ) : policyListRows.length > 0 ? (
                  <BsdsCard
                    className="w-full min-w-0 p-4"
                    role="region"
                    aria-label="Selecionar Apólices para criar representação"
                  >
                    <div className="bsds-display-flex bsds-align-items-center bsds-mb-16">
                      <div className="w-[2.5px] h-6 bg-red-600 bsds-mr-12" aria-hidden="true" />
                      <h4 className="opacity-80 bsds-mb-0">Apólices para nova representação</h4>
                    </div>
                    <div className="w-full min-h-[150px] overflow-x-auto custom-scrollbar rounded-md shadow-xs">
                      <div>
                        <table
                          className="min-w-[800px] w-full"
                          role="table"
                          aria-label="Apólices para nova representação"
                        >
                          <thead>
                            <tr>
                              <th className="px-4 py-2 text-left text-sm font-semibold border-b border-gray-100 w-16" scope="col"> </th>
                              <th className="px-4 py-2 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap" scope="col">Nº Apólice</th>
                              <th className="px-4 py-2 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap" scope="col">Produto</th>
                              <th className="px-4 py-2 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap" scope="col">Vigência</th>
                              <th className="px-4 py-2 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap min-w-[140px]" scope="col">Chave legado (preview)</th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-50 text-text-color">
                            {policyListRows.map((row, idx) => {
                              const optKey = policyListRowKey(row, idx);
                              const checked = selectedNewPolicyKeys.includes(optKey);
                              const newChecking = policyCheckByNewKey[optKey]?.checking === true;
                              const newBlocked = policyCheckByNewKey[optKey]?.cpfJaVinculado === true;
                              const preview =
                                selectedType && row.numApoliceApol != null && (cnpjDigitsForChave.length === 11 || cnpjDigitsForChave.length === 14)
                                  ? buildEstpChaveLegado(
                                    cnpjDigitsForChave,
                                    row.numApoliceApol,
                                    selectedTypeToRamoDigit(selectedType)
                                  )
                                  : '—';
                              return (
                                <tr key={optKey} className="hover:bg-gray-50 transition-colors text-xs">
                                  <td className="px-4 py-2 align-top">
                                    <BsdsCheckbox
                                      checked={checked}
                                      onBsdsChange={() => {
                                        void (async () => {
                                          if (selectedNewPolicyKeys.includes(optKey)) {
                                            setSelectedNewPolicyKeys((prev) =>
                                              prev.filter((k) => k !== optKey)
                                            );
                                            setPolicyCheckByNewKey((p) => {
                                              const next = { ...p };
                                              delete next[optKey];
                                              return next;
                                            });
                                            return;
                                          }
                                          if (row.numApoliceApol == null) return;
                                          setPolicyCheckByNewKey((p) => ({
                                            ...p,
                                            [optKey]: { cpfJaVinculado: false, checking: true },
                                          }));
                                          const res = await checkVinculoParaApolice(row);
                                          const blocked = res?.cpfJaVinculadoNestaChave ?? false;
                                          setPolicyCheckByNewKey((p) => ({
                                            ...p,
                                            [optKey]: { cpfJaVinculado: blocked, checking: false },
                                          }));
                                          if (blocked) return;
                                          setSelectedNewPolicyKeys((prev) => [...prev, optKey]);
                                        })();
                                      }}
                                      disabled={isLoading || isSuccess || newChecking}
                                      color="primary"
                                      aria-label={`Selecionar Apólice ${row.numApoliceApol ?? ''}`}
                                    />
                                  </td>
                                  <td className="px-4 py-2">{row.numApoliceApol != null ? String(row.numApoliceApol) : '—'}</td>
                                  <td className="px-4 py-2">{row.nomeProdutoApol ?? row.nomeRamoApol ?? '—'}</td>
                                  <td className="px-4 py-2 whitespace-nowrap">
                                    {formatOpeningDateForDisplay(row.dataInicioVigencia) || '—'} — {formatOpeningDateForDisplay(row.dataFimVigencia) || '—'}
                                  </td>
                                  <td className="px-4 py-2 align-top">
                                    <span className="block break-all text-[10px] text-gray-700">{preview}</span>
                                    {newChecking ? (
                                      <span className="block text-[10px] text-gray-600 mt-1">Verificando…</span>
                                    ) : newBlocked ? (
                                      <span className="block text-[10px] text-red-700 mt-1 font-medium">
                                        CPF já vinculado a esta representação
                                      </span>
                                    ) : null}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </BsdsCard>
                ) : null}
                
                <BsdsCard
                  className="w-full min-w-0 p-4"
                  role="region"
                  aria-label="Apólices do documento informado (listar-apol-perf-usuario)"
                >
                  <div className="bsds-display-flex bsds-align-items-center bsds-mb-16">
                    <h5 className="opacity-80 bsds-mb-0">Apólices disponíveis no Produto (referente ao documento informado)</h5>
                  </div>
                  <div className="w-full min-h-[150px] overflow-x-auto custom-scrollbar rounded-md shadow-xs">
                    <div>
                      <table
                        className="min-w-full"
                        role="table"
                        aria-label="Apólices disponíveis do documento informado"
                      >
                        <thead>
                          <tr>
                            <th className="px-4 py-2 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap" scope="col">Nº Apólice</th>
                            <th className="px-4 py-2 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap" scope="col">Produto</th>
                            <th className="px-4 py-2 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap" scope="col">Início vig.</th>
                            <th className="px-4 py-2 text-left text-sm font-semibold border-b border-gray-100 whitespace-nowrap" scope="col">Fim vig.</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-50 text-text-color">
                          {paginatedBusinessLinks.length === 0 ? (
                            <tr>
                              <td colSpan={4} className="px-4 py-4 text-center text-text-color text-xs" role="status" aria-live="polite">
                                Nenhuma Apólice encontrada para o documento informado
                              </td>
                            </tr>
                          ) : (
                            paginatedBusinessLinks.map((link) => {
                              const pol = findPolicyByRowKey(link.id);
                              return (
                                <tr key={link.id} className="hover:bg-gray-50 transition-colors text-xs">
                                  <td className="px-4 py-2">{link.number}</td>
                                  <td className="px-4 py-2">{link.description}</td>
                                  <td className="px-4 py-2 whitespace-nowrap">{formatOpeningDateForDisplay(pol?.dataInicioVigencia) || '—'}</td>
                                  <td className="px-4 py-2 whitespace-nowrap">{formatOpeningDateForDisplay(pol?.dataFimVigencia) || '—'}</td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {!isLoading && businessLinksForTable.length > 0 && (
                    <div className="bsds-mt-24">
                      <Pagination
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={handlePageChange}
                        totalItems={businessLinksForTable.length}
                        itemLabel="Apólices"
                        alwaysShow
                      />
                    </div>
                  )}
                </BsdsCard>
              </div>
            </div>
          </div>
        </Visibility>

        {/* Buttons at the bottom of the form without fixed */}
        <div
          className="bsds-display-flex bsds-justify-content-center bsds-flex-wrap mt-auto flex justify-center items-center flex-col sm:flex-row gap-6 align-items-center bsds-width-100 lg:bsds-width-50 mb-4"
          role="region"
          aria-label="Ações do formulário"
        >

          {/* Step 1: CNPJ — Cancel and Continue */}
          {currentStep === 1 && (cnpjData && !showCpfSearch && !showCpfInput && !isSuccess) && (
            <>
              <Button onClick={handleCancel} type="button" color="primary" fill="outline"
                className="bsds-width-100 order-2 sm:order-1"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleContinue}
                type="button"
                color="primary"
                fill="solid"
                className="bsds-width-100 bsds-sm-width-auto order-1 sm:order-2"
                aria-label="Continuar para o próximo passo: cadastro do CPF"
              >
                Continuar
              </Button>
            </>
          )}

          {/* Step 2: CPF — Back and Continue */}
          {currentStep === 2 && showCpfInput && !isSuccess && (
            <>
              <Button onClick={handleBackFromCpf} type="button" color="primary" fill="outline" className="bsds-width-100 bsds-sm-width-auto order-2 sm:order-1">
                Voltar
              </Button>
              <Button
                onClick={() => void handleContinueToVincule()}
                disabled={(() => {
                  if (isLoading) return true;
                  const hasCpf = (cpfInput || cpfSearch).trim().length > 0;
                  if (!hasCpf || !cpfData) return true;
                  if (pfExistedInCadp) {
                    if (SKIP_CORPORATE_EMAIL_STEP) return false;
                    return !formData.corporateEmail.trim();
                  }
                  const base =
                    formData.name.trim() &&
                    formData.email.trim() &&
                    formData.phone.trim() &&
                    formData.birthDate.trim() &&
                    formData.nomeMae.trim();
                  return !base;
                })()}
                type="button"
                color="primary"
                fill="solid"
                className="bsds-width-100 bsds-sm-width-auto order-1 sm:order-2"
                aria-label={
                  isLoading
                    ? 'Processando...'
                    : pfExistedInCadp
                      ? 'Continuar para listar Apólices e vincular representante'
                      : 'Salvar dados do Segurado e continuar para listar Apólices'
                }
              >
                {pfExistedInCadp ? 'Continuar' : 'Salvar e continuar'}
              </Button>
            </>
          )}

          {/* Step 3: Back and Finalize */}
          {currentStep === 3 && !isSuccess && (
            <>
              <Button onClick={handleBackFromVincule} type="button" color="primary" fill="outline" className="bsds-width-100 bsds-sm-width-auto order-2 sm:order-1">
                Voltar
              </Button>
              <Button
                onClick={() => void handleFinalize()}
                disabled={(() => {
                  if (isLoading) return true;
                  if (!user?.idPessoa) return true;
                  return !step3PairingComplete;
                })()}
                type="button"
                color="primary"
                fill="solid"
                loading={isLoading}
                className="bsds-width-100 bsds-sm-width-auto order-1 sm:order-2"
                aria-label={
                  isLoading
                    ? 'Salvando vínculos...'
                    : !step3PairingComplete
                      ? 'Associe ao menos um vínculo a uma Apólice para finalizar'
                      : 'Finalizar cadastro do representante'
                }
              >
                Finalizar
              </Button>
            </>
          )}

          {/* Success state */}
          {isSuccess && (
            <Button onClick={handleCancel} type="button" color="primary" fill="solid" className="bsds-width-100 bsds-sm-width-auto">
              Fechar
            </Button>
          )}
        </div>
      </div>

    </div>
  );
};

export default RegisterPolicyholderPage;